/**
 * BreadcrumbBar
 *
 * Appears at the app level and relies on routes.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { filter, replace } from 'lodash';
import classNames from 'classnames';
import styles from './styles.css';

// Components
import Icon from 'components/Icon';
import Link from 'components/Link';

// Config and language
import { common } from 'language';

// Animation settings

const BreadcrumbBar = (props) => {
	const { setMenu, setSubMenu } = props;


	/**
	 * Generate top level menu items from routes
	 */
	const primaryItems = [];

	// Collect only routes with the `breadcrumbBar` property set to true.
	const breadcrumbItems = filter(props.routes, (route) => route.breadcrumbBar);

	breadcrumbItems && breadcrumbItems.forEach((route, index) => {
		if (!props.isMobileSiteMenu || (props.isMobileSiteMenu && route.showMobile)) {
			const labelPrefix = props.title || '';
			const label = index === 0 && labelPrefix ? `${labelPrefix} ${route.label}` : route.label;
			const primaryLinkClass =
				props.location.pathname === (route.opensMenu ? null : route.path) || // nav item matches pathname
				props.selectedMenu === route.name // the `selectedMenu` prop matches the route name
					? styles['primaryItem--here']
					: styles.primaryItem;

			const outgoing = {
				onClick: route.opensMenu
					? () => {
						setSubMenu('');
						setMenu(route.name);
					}
					: null,
				to: route.opensMenu ? null : route.path
			};

			primaryItems.push(
				<li className={ primaryLinkClass } key={ route.name }>
					<Link { ...outgoing } className={ styles.primaryLink }>
						{ label }
					</Link>
				</li>
			);
		}
	});

	// Current route's path
	const path = props.current.path;
	let showSecondary = path !== '/' && path !== undefined || props.showSecondary;

	// If the submenu for 'browse by style' or 'find by name' is open,
	// then use the 'showMobileFontsMenu' prop to control whether to hide/show
	// the back to fonts button in breadcrumb
	if (props.menu === 'fonts' && props.isMobileSiteMenu) {
		showSecondary = props.showMobileFontsMenu;
	}

	const generateTypeCapsuleLabel = (currentLabel, breakpoints) => {
		if (breakpoints.gtSmallMedium) {
			return <>{currentLabel} <span>Type Capsule</span></>;
		} else if (breakpoints.gtSmall) {
			return <>{currentLabel} <span>Capsule</span></>;
		}
		return `“${currentLabel}”`;
	};

	let currentLabel = props.current.label;
	const { currentBlogTitle, location } = props;

	if (props.isMobileSiteMenu && props.current.shortLabel) {
		currentLabel = props.current.shortLabel;
	}

	// If current label value is all caps (e.g. FAQ) don't modify. If path matches
	// /blog/tag/ extract breadcrumb current label from the path value after
	// stripping /blog/tag/ from the value
	const regexPatternBlogTag = new RegExp(/^\/blog\/tag\//, 'g');
	if (regexPatternBlogTag.exec(path) !== null) {
		const truncatedTag = path.replace(regexPatternBlogTag, '');

		if (truncatedTag.indexOf('+') > -1) {
			currentLabel = currentLabel;
		} else {
			currentLabel = replace(currentLabel, new RegExp(/h&co/, 'g'), common.companyNameShort);
		}
	} else if (!!props.current.label && (props.current.label).toUpperCase() === props.current.label) {
		currentLabel = props.current.label;
	}

	if (props.current.typeCapsule === 1) {
		currentLabel = generateTypeCapsuleLabel(currentLabel, props.breakpoints);
	}

	const parentIsBlog = (!!props.parent && !!props.parent.label && props.parent.label.toLowerCase().indexOf('blog') > -1);

	return (
		<nav data-wrapper className={ classNames(styles.breadcrumb, props.className, { [styles.showSecondary]: showSecondary }) }>
			<ul
				className={ styles.primary }
			>
				{ primaryItems }
			</ul>
			<div
				className={ styles.secondary }
			>
				<Link
					data-back-button
					className={ styles.back }
					onClick={ () => {
						if (props.onclick) { props.onclick(); }
						props.onSetTertiaryMenu(null);
					} }
					to={ props.parent ? props.parent.path : '' }
				>
					<Icon className={ styles.backIcon } iconName="back" title="Go back" />
					{ props.parent && props.parent.label }
				</Link>
				<div className={ styles.label }>
					{ location.pathname.includes('/blog') ? currentBlogTitle || currentLabel : currentLabel }
					{ props.current.tagLine &&
						<span className={ styles.tagLine }> { props.current.tagLine }</span>
					}
				</div>
				{ !parentIsBlog && props.menu !== 'fonts' && props.tertiaryMenu &&
					<div
						data-test-tertiarymenu
						className={ styles.tertiaryMenu }
						onClick={ () => {
							props.onSetTertiaryMenu(props.tertiaryMenu);
						} }
						role="button"
						tabIndex="0"
					>
						{ props.tertiaryMenuLabel }
					</div>
				}
				{ props.tertiaryMenuLink && !props.tertiaryMenu &&
					<Link className={ styles.tertiaryMenu } to={ props.tertiaryMenuLink }>{ props.tertiaryMenuLabel }</Link>
				}
				{ props.ctaLink && props.location.pathname !== props.ctaLink &&
				<Link className={ classNames(styles.cta) } to={ props.ctaLink }>{ props.ctaLabel }</Link>
				}
				{ props.ctaLink && props.location.pathname === props.ctaLink &&
				<span className={ classNames(styles.ctaDisabled) }>{ props.ctaLabel }</span>
				}
			</div>
		</nav>
	);
};

BreadcrumbBar.propTypes = {
	breakpoints: PropTypes.object,
	className: PropTypes.string,
	ctaLink: PropTypes.string,
	ctaLabel: PropTypes.string,
	current: PropTypes.object,
	isMobileSiteMenu: PropTypes.bool,
	location: PropTypes.object.isRequired,
	menu: PropTypes.string,
	onSetTertiaryMenu: PropTypes.func,
	onclick: PropTypes.func,
	parent: PropTypes.object,
	routes: PropTypes.array.isRequired,
	selectedMenu: PropTypes.string,
	setMenu: PropTypes.func.isRequired,
	setSubMenu: PropTypes.func.isRequired,
	showMobileFontsMenu: PropTypes.bool,
	showSecondary: PropTypes.bool,
	tertiaryMenu: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.array
	]),
	tertiaryMenuLink: PropTypes.string,
	tertiaryMenuLabel: PropTypes.string,
	title: PropTypes.string
};

BreadcrumbBar.defaultProps = {
	breakpoints: null,
	className: null,
	ctaLink: null,
	ctaLabel: 'Buy',
	current: null,
	isMobileSiteMenu: false,
	menu: null,
	onclick: null,
	onSetTertiaryMenu: null,
	parent: null,
	selectedMenu: null,
	showSecondary: false,
	showMobileFontsMenu: false,
	tertiaryMenu: null,
	tertiaryMenuLink: null,
	tertiaryMenuLabel: 'Details',
	title: null
};

BreadcrumbBar.displayName = 'BreadcrumbBar';

export default BreadcrumbBar;
